import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, ShieldCheck } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { ENFORCE_2FA } from "@/lib/twoFactor";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Drugi krok — 2FA (TOTP) dla kont które JUŻ mają zweryfikowany czynnik
  const [step, setStep] = useState<"password" | "code">("password");
  const [factorId, setFactorId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resolving, setResolving] = useState(false);

  const { signIn, signOut, user } = useAuth();
  const navigate = useNavigate();

  // Po zalogowaniu (user w kontekście) decydujemy dokąd na podstawie AAL:
  //   aal2                       → wpuść do panelu
  //   aal1 + zweryfikowany czynnik → krok kodu (tutaj, bez nawigacji)
  //   aal1 + brak czynnika        → obowiązkowa konfiguracja /setup-2fa
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setResolving(true);
      try {
        if (!ENFORCE_2FA) {
          navigate("/", { replace: true });
          return;
        }

        const { data: aal, error: aalErr } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
        if (cancelled) return;
        if (aalErr || !aal) {
          setError("Nie udało się sprawdzić statusu 2FA. Odśwież stronę.");
          return;
        }

        if (aal.currentLevel === "aal2") {
          navigate("/", { replace: true });
          return;
        }

        if (aal.nextLevel === "aal2") {
          // Konto ma 2FA — potrzebny kod. (listFactors zwraca zweryfikowany czynnik.)
          const { data: factors } = await supabase.auth.mfa.listFactors();
          if (cancelled) return;
          const verified = (factors?.totp ?? []).find((f) => f.status === "verified");
          if (!verified) {
            navigate("/setup-2fa", { replace: true });
            return;
          }
          setFactorId(verified.id);
          setStep("code");
          return;
        }

        // nextLevel === 'aal1' → brak 2FA → obowiązkowa konfiguracja
        navigate("/setup-2fa", { replace: true });
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (!email || !password) {
      setError("Email i hasło są wymagane");
      setLoading(false);
      return;
    }

    try {
      const { error } = await signIn(email, password);

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          setError("Nieprawidłowy email lub hasło");
        } else {
          setError(error.message);
        }
      }
      // Sukces → onAuthStateChange ustawi `user` → useEffect wyżej zdecyduje dokąd.
    } catch (err) {
      setError("Wystąpił nieoczekiwany błąd");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!factorId || verifying) return;
    const trimmed = code.trim();
    if (!/^\d{6}$/.test(trimmed)) {
      setError("Wpisz 6-cyfrowy kod z aplikacji.");
      return;
    }
    setVerifying(true);
    setError("");
    try {
      // Challenge tworzymy dopiero teraz — nie zdąży wygasnąć.
      const { data: challenge, error: chErr } = await supabase.auth.mfa.challenge({ factorId });
      if (chErr || !challenge) {
        setError("Nie udało się rozpocząć weryfikacji. Spróbuj ponownie.");
        return;
      }
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: trimmed,
      });
      if (vErr) {
        setError("Nieprawidłowy kod. Spróbuj ponownie.");
        setCode("");
        return;
      }
      navigate("/", { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Błąd weryfikacji.");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {/* Logo i nazwa aplikacji */}
          <div className="flex flex-col items-center gap-3 mb-4">
            <div className="flex items-center gap-3">
              <img
                src="/logos/logo.png"
                alt="PBNSD666 Logo"
                className="h-20 w-20 object-contain"
                onError={(e) => {
                  // Fallback jeśli logo nie istnieje
                  e.currentTarget.style.display = 'none';
                }}
              />
              <CardTitle className="text-3xl font-bold text-foreground">
                PBNSD666
              </CardTitle>
            </div>
            <div className="text-sm text-muted-foreground">
              Panel logowania
            </div>
          </div>

          <CardDescription className="text-base">
            {step === "code" ? "Weryfikacja dwuskładnikowa" : "Zaloguj się do swojego konta"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {step === "code" ? (
            <form onSubmit={handleVerifyCode} className="space-y-4">
              <div className="flex flex-col items-center gap-2 text-center">
                <ShieldCheck className="h-8 w-8 text-primary" />
                <p className="text-sm text-muted-foreground">
                  Wpisz 6-cyfrowy kod z aplikacji uwierzytelniającej.
                </p>
              </div>

              <Input
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="123456"
                className="mx-auto w-44 text-center font-mono text-2xl tracking-widest"
                autoFocus
              />

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" className="w-full" disabled={verifying || code.length !== 6}>
                {verifying ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {verifying ? "Weryfikuję…" : "Potwierdź"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => signOut()}
              >
                Wyloguj się
              </Button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="twoj@email.pl"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Hasło</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                />
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={loading || resolving}
              >
                {(loading || resolving) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {loading ? "Logowanie..." : resolving ? "Sprawdzam 2FA..." : "Zaloguj się"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Auth;
